import axios from "axios";
import { clearCache } from "@/lib/swrCache";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth rides an HttpOnly session cookie the backend sets on login; the browser attaches it
// automatically via withCredentials. The token is never exposed to JS, so a stray script
// can't read it. Clear any token left behind by the old localStorage scheme.
try { localStorage.removeItem("shs_token"); } catch { /* storage disabled — nothing to clear */ }

export const api = axios.create({ baseURL: API, withCredentials: true });

// Privacy: stop the Emergent-injected PostHog from recording/capturing the financial
// admin + clock screens. Called from those pages on mount (survives rebuilds).
export const disableAnalyticsHere = () => {
  try {
    const ph = typeof window !== "undefined" ? window.posthog : null;
    if (!ph) return;
    ph.stopSessionRecording?.();
    ph.opt_out_capturing?.();
  } catch { /* analytics absent — nothing to disable */ }
};


// Videos are detected by the file extension in their storage path.
export const isVideoPath = (p) => /\.(mp4|mov|webm|m4v)(\?|#|$)/i.test(p || "");

// Direct URL for streaming (videos use Range requests — never blob-load a clip). Auth rides
// the HttpOnly session cookie, so the token never appears in a URL.
export const directFileUrl = (path) => `${API}/files/${path}`;

// Desktop browsers often report iPhone .HEIC photos (and sometimes .mov clips) with an
// empty or generic octet-stream type — fall back to the file extension so those files
// still route and validate correctly.
const TYPE_BY_EXT = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp",
  heic: "image/heic", heif: "image/heif", gif: "image/gif",
  mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm", m4v: "video/x-m4v",
};

export const fileMediaType = (file) => {
  const t = (file?.type || "").toLowerCase();
  if (t && t !== "application/octet-stream") return t;
  const ext = (file?.name || "").split(".").pop().toLowerCase();
  return TYPE_BY_EXT[ext] || t || "";
};

// Photos upload in one request; videos are sliced into 8 MB chunks so big phone clips
// get through the proxy cap. Each chunk retries up to 3 times — cell signal on a job
// site drops packets constantly, and one lost chunk shouldn't kill a 40-chunk upload.
// Returns { path, size } either way.
const MAX_PHOTO_BYTES = 25 * 1024 * 1024;
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;

// Big phone photos (ProRAW, hi-res HEIC exports) get shrunk on-device before upload:
// max 2560px on the long edge, JPEG quality stepped down until the file is comfortably
// small. Undecodable formats (HEIC in some browsers) fall through untouched — the
// server cap is the backstop.
const SHRINK_THRESHOLD = 8 * 1024 * 1024;
const SHRINK_TARGET = 8 * 1024 * 1024;

const shrinkPhotoIfNeeded = async (file) => {
  if (!(file.type || "").startsWith("image/") || file.size <= SHRINK_THRESHOLD) return file;
  try {
    let bmp;
    try {
      bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      bmp = await createImageBitmap(file);
    }
    const scale = Math.min(1, 2560 / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    let best = null;
    for (const q of [0.85, 0.72, 0.6]) {
      const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", q));
      if (!blob) continue;
      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= SHRINK_TARGET) break;
    }
    if (best && best.size < file.size) {
      const base = (file.name || "photo").replace(/\.\w+$/, "");
      return new File([best], `${base}.jpg`, { type: "image/jpeg" });
    }
  } catch { /* can't decode this format here — upload the original */ }
  return file;
};

const mb = (n) => Math.round(n / 1024 / 1024);
// Client-side rejections (0-byte iCloud placeholder, oversize) — callers can tell these from
// network failures via err.code and show the message instead of retrying.
const invalidUpload = (message) => Object.assign(new Error(message), { code: "upload_invalid" });

// Customer "photo of the problem": always shrunk on the phone (1600px long edge, ~400 KB JPEG)
// so a slow cell connection still gets it through. Undecodable formats upload untouched.
const PROBLEM_EDGE = 1600;
const PROBLEM_TARGET = 450 * 1024;
// Check photos: a little bigger so the amount, date and check number stay readable (~1 MB JPEG)
export const CHECK_PHOTO = { edge: 2000, target: 900 * 1024 };
export const compressPhoto = async (file, { edge = PROBLEM_EDGE, target = PROBLEM_TARGET } = {}) => {
  try {
    let bmp;
    try {
      bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      bmp = await createImageBitmap(file);
    }
    const scale = Math.min(1, edge / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff"; // transparent PNGs would turn black as JPEG
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close?.();
    let best = null;
    for (const q of [0.82, 0.7, 0.58]) {
      const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", q));
      if (blob && (!best || blob.size < best.size)) best = blob;
      if (blob && blob.size <= target) break;
    }
    if (best && best.size < file.size) {
      const base = (file.name || "photo").replace(/\.\w+$/, "");
      return new File([best], `${base}.jpg`, { type: "image/jpeg" });
    }
  } catch { /* can't decode this format here — upload the original */ }
  return file;
};

const xhrPost = (url, body, headers, onProgress) => new Promise((resolve, reject) => {
  const x = new XMLHttpRequest();
  x.open("POST", url);
  Object.entries(headers || {}).forEach(([k, v]) => x.setRequestHeader(k, v));
  x.timeout = 120000;
  x.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
  x.onload = () => {
    let data = null;
    try { data = JSON.parse(x.responseText); } catch { /* non-JSON error page */ }
    resolve({ status: x.status, data });
  };
  x.onerror = () => reject(new Error("network"));
  x.ontimeout = () => reject(new Error("timeout"));
  x.send(body);
});

// Compress + upload one problem photo with progress (0..1). Same multipart → raw-bytes
// fallback as uploadMedia. Throws a friendly Error on failure.
export const uploadProblemPhoto = async (file, onProgress) => {
  if (file.size === 0) {
    throw invalidUpload("Still downloading from iCloud — wait a few seconds and pick it again.");
  }
  const photo = await compressPhoto(file);
  if (photo.size > MAX_PHOTO_BYTES) throw invalidUpload(`That photo is ${mb(photo.size)} MB — too big to send.`);
  let r;
  try {
    const fd = new FormData();
    fd.append("file", photo, photo.name || "photo.jpg");
    r = await xhrPost(`${API}/uploads`, fd, null, onProgress);
    if (r.status === 422) {
      const ptype = fileMediaType(photo) || "image/jpeg";
      r = await xhrPost(
        `${API}/uploads/raw?filename=${encodeURIComponent(photo.name || "photo.jpg")}&content_type=${encodeURIComponent(ptype)}`,
        photo, { "Content-Type": "application/octet-stream" }, onProgress
      );
    }
  } catch {
    throw new Error("No connection — tap to retry.");
  }
  if (r.status === 429) throw new Error("Too many uploads at once — wait a minute, then tap to retry.");
  if (r.status < 200 || r.status >= 300) {
    throw new Error(r.data?.detail ? formatApiErrorDetail(r.data.detail) : "Upload failed — tap to retry.");
  }
  return r.data;
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const uploadErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail) return formatApiErrorDetail(detail);
  if (err?.message && !err?.response) return err.message;
  return "Upload failed — please try again.";
};

const PHOTO_TRIES = 3;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// A cellular drop rejects fetch with a bare TypeError ("Load failed") — worth another go, like a 5xx.
const retryableUpload = (err) => !err?.response || err.response.status >= 500 || err.response.status === 408;

const postPhoto = async (photo, signal) => {
  // Plain fetch instead of axios — a couple of desktop browser/extension combos mangle
  // axios's multipart serialization ("Field required" 422s); fetch is native and reliable.
  const fd = new FormData();
  fd.append("file", photo, photo.name || "photo.jpg");
  if (!fd.has("file")) {
    throw invalidUpload("The photo didn't attach — close the dialog and pick it again.");
  }
  let resp = await fetch(`${API}/uploads`, { method: "POST", body: fd, signal });
  if (resp.status === 422) {
    // Something between this browser and the server stripped the multipart body
    // (antivirus web-filter class of problem) — retry with the raw file bytes instead.
    const ptype = fileMediaType(photo) || photo.type || "image/jpeg";
    resp = await fetch(
      `${API}/uploads/raw?filename=${encodeURIComponent(photo.name || "photo.jpg")}&content_type=${encodeURIComponent(ptype)}`,
      { method: "POST", headers: { "Content-Type": "application/octet-stream" }, body: photo, signal }
    );
  }
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw { response: { status: resp.status, data: body } };
  }
  return resp.json();
};

// timeoutMs (photos only): give up after that long so weak signal falls back to the phone queue fast.
// Without it a photo gets PHOTO_TRIES attempts (onRetry(n) before try n; stop() true = no more tries).
// compress: shrink to that size first.
export const uploadMedia = async (file, onChunk, { timeoutMs, compress, onRetry, stop } = {}) => {
  // iCloud/Photos hand Safari a 0-byte placeholder while the real file downloads —
  // that's exactly "picked it, failed twice, worked on the third try".
  if (file.size === 0) {
    throw invalidUpload("That file is still downloading from iCloud — give it a few seconds and pick it again.");
  }
  const mediaType = fileMediaType(file);
  if (!mediaType.startsWith("video/")) {
    const photo = compress ? await compressPhoto(file, compress) : await shrinkPhotoIfNeeded(file);
    if (photo.size > MAX_PHOTO_BYTES) {
      throw invalidUpload(`That photo is ${mb(photo.size)} MB — the limit is 25 MB. Retake it at a lower resolution or screenshot it.`);
    }
    if (timeoutMs) {
      // an aborted fetch rejects with no response → callers treat it as no signal
      // (manual controller: AbortSignal.timeout is missing on older iPhones)
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), timeoutMs);
      try { return await postPhoto(photo, ctrl.signal); } finally { clearTimeout(timer); }
    }
    for (let attempt = 1; ; attempt++) {
      try {
        return await postPhoto(photo);
      } catch (err) {
        if (attempt >= PHOTO_TRIES || err?.code === "upload_invalid" || !retryableUpload(err)) throw err;
        onRetry?.(attempt + 1);
        await sleep(1500 * attempt);
        if (stop?.()) throw err;
      }
    }
  }
  if (file.size > MAX_VIDEO_BYTES) {
    throw invalidUpload(`That video is ${mb(file.size)} MB — the limit is 500 MB. Keep walkthrough clips under about a minute.`);
  }
  const CHUNK = 8 * 1024 * 1024;
  const { data: init } = await api.post("/uploads/init", {
    filename: file.name || "video.mp4",
    content_type: mediaType,
    size: file.size,
  });
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const sendWithRetry = async (fn) => {
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        return await fn();
      } catch (err) {
        lastErr = err;
        // 4xx (other than 408/429) is a definitive rejection — retrying won't help
        const s = err?.response?.status;
        if (s && s < 500 && s !== 408 && s !== 429) throw err;
        await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
      }
    }
    throw lastErr;
  };
  for (let i = 0; i < total; i++) {
    await sendWithRetry(() =>
      api.post(`/uploads/${init.upload_id}/chunk?index=${i}`, file.slice(i * CHUNK, (i + 1) * CHUNK), {
        headers: { "Content-Type": "application/octet-stream" },
      })
    );
    onChunk?.(i + 1, total);
  }
  const { data } = await sendWithRetry(() => api.post(`/uploads/${init.upload_id}/complete`));
  return data;
};
// Expired/invalid session → drop the dead token and send the user to the staff login.
// Auth endpoints are excluded — their 401s are business errors ("wrong current password"),
// not expired sessions, and must surface as inline toasts instead of a forced logout.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Expired/invalid session → bounce to the staff login, but ONLY from the in-app
    // (admin/clock) pages. Auth endpoints' 401s are business errors ("wrong current
    // password"), and public pages make unauthenticated calls that must not redirect.
    const isAuthEndpoint = (error.config?.url || "").includes("/auth/");
    const path = window.location.pathname;
    const inApp = path.startsWith("/admin") || path.startsWith("/clock");
    if (error.response?.status === 401 && inApp && !isAuthEndpoint && !path.startsWith("/admin/login")) {
      clearCache();
      window.location.assign("/admin/login");
    }
    return Promise.reject(error);
  }
);
